import { useState, useMemo, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Typography, Grid } from "@mui/material";

import FilterBtn from "../../../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../../../Assets/Icons/btn-filters-up.png";
import { useNotification } from "../../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../../Hooks/useApiErrorHandler";
import httpclient from "../../../../../Utils";
import { createFilterHandler } from "../../../../../Utils/FilterUtils";

import { columns } from "./columns";
import { rows } from "./data";
import Filter from "./Filter";
import EfficiencyLogsModal from "./EfficiencyLogsModal";

const dataGridCustomStyles = {
  border: "none",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f5f5f5",
    borderRadius: 1,
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #f0f0f0",
    color: "#383E49",
    fontWeight: 500,
  },
  "& .onhold-row": {
    backgroundColor: "#fff8f8",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontFamily: "lexend",
    fontSize: "14px",
    fontWeight: 500,
    color: "#667085",
  },
};

const INITIAL_FILTER_STATE = {
  employeeName: null,
  deviceImei: "",
  pickupStarted: "",
  totalOrdersMin: "",
  totalOrdersMax: "",
  orderLinesMin: "",
  orderLinesMax: "",
  totalItemsMin: "",
  totalItemsMax: "",
  timePeriod: "",
};

const fieldMappings = {
  employeeName: ["employeeName", "$eq"],
  deviceImei: ["deviceImei", "$contains"],
  timePeriod: ["timePeriod", "$eq"],
};

const rangeFields = {
  totalOrders: {
    apiField: "totalOrders",
    minField: "totalOrdersMin",
    maxField: "totalOrdersMax",
  },
  orderLines: {
    apiField: "orderLines",
    minField: "orderLinesMin",
    maxField: "orderLinesMax",
  },
  totalItems: {
    apiField: "totalItems",
    minField: "totalItemsMin",
    maxField: "totalItemsMax",
  },
};

export default function PickerEfficiency() {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedPicker, setSelectedPicker] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});

  const [loading, setLoading] = useState(false);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const fetchEfficiencyData = async () => {
    const API_URL = "getPlugins.php";

    try {
      const res = await httpclient.get(API_URL);

      const { data } = res;

      if (data.status === 200) {
        setData(data.data);
      } else {
        showNotification("error", data.msg);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // fetchEfficiencyData();
  }, []);

  const handleRowClick = (params) => {
    setSelectedPicker(params.row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChangeFilter = (field) => (event, value) => {
    setFilterData((prev) => ({
      ...prev,
      [field]:
        value ||
        (event && event.target.value) ||
        (field === "employeeName" ? null : ""),
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: setData,
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: "getPlugins.php",
    fieldMappings,
    rangeFields,
  });

  const onFilterClick = () => {
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: field === "employeeName" ? null : "",
    }));
    setSubmittedData((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (
        submittedData.employeeName &&
        !row.employeeName.includes(submittedData.employeeName)
      ) {
        return false;
      }

      if (
        submittedData.deviceImei &&
        !row.deviceImei
          .toLowerCase()
          .includes(submittedData.deviceImei.toLowerCase())
      ) {
        return false;
      }

      if (
        submittedData.pickupStarted &&
        !row.pickupStarted
          .toLowerCase()
          .includes(submittedData.pickupStarted.toLowerCase())
      ) {
        return false;
      }

      const totalOrders = Number(row.totalOrders);
      if (
        submittedData.totalOrdersMin &&
        totalOrders < Number(submittedData.totalOrdersMin)
      ) {
        return false;
      }

      if (
        submittedData.totalOrdersMax &&
        totalOrders > Number(submittedData.totalOrdersMax)
      ) {
        return false;
      }

      const orderLines = Number(row.orderLines);

      if (
        submittedData.orderLinesMin &&
        orderLines < Number(submittedData.orderLinesMin)
      ) {
        return false;
      }
      if (
        submittedData.orderLinesMax &&
        orderLines > Number(submittedData.orderLinesMax)
      ) {
        return false;
      }

      const totalItems = Number(row.totalItems);

      if (
        submittedData.totalItemsMin &&
        totalItems < Number(submittedData.totalItemsMin)
      ) {
        return false;
      }

      if (
        submittedData.totalItemsMax &&
        totalItems > Number(submittedData.totalItemsMax)
      ) {
        return false;
      }

      if (submittedData.timePeriod) {
        const pickupDate = new Date(row.pickupStarted);
        const currentDate = new Date("2025-04-14");
        let startDate;

        switch (submittedData.timePeriod) {
          case "Today":
            startDate = new Date(currentDate);
            startDate.setHours(0, 0, 0, 0);
            return (
              pickupDate >= startDate &&
              pickupDate <=
                new Date(currentDate.getTime() + 24 * 60 * 60 * 1000 - 1)
            );
          case "7 Days":
            startDate = new Date(currentDate);
            startDate.setDate(currentDate.getDate() - 7);
            startDate.setHours(0, 0, 0, 0);
            return pickupDate >= startDate && pickupDate <= currentDate;
          case "14 Days":
            startDate = new Date(currentDate);
            startDate.setDate(currentDate.getDate() - 14);
            startDate.setHours(0, 0, 0, 0);
            return pickupDate >= startDate && pickupDate <= currentDate;
          case "1 Month":
            startDate = new Date(currentDate);
            startDate.setMonth(currentDate.getMonth() - 1);
            startDate.setHours(0, 0, 0, 0);
            return pickupDate >= startDate && pickupDate <= currentDate;
          case "1 Year":
            startDate = new Date(currentDate);
            startDate.setFullYear(currentDate.getFullYear() - 1);
            startDate.setHours(0, 0, 0, 0);
            return pickupDate >= startDate && pickupDate <= currentDate;
          default:
            return true;
        }
      }

      return true;
    });
  }, [submittedData]);

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          backgroundColor: "#fff",
          padding: "24px 16px",
          borderRadius: "8px",
        }}
      >
        <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "lexend",
                fontSize: "16px",
                fontWeight: 500,
                color: "#5D6679",
              }}
            >
              Efficiency Pickup Report by User
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <img
              src={filterOpen ? FilterBtnUp : FilterBtn}
              alt="Filter"
              style={{ cursor: "pointer" }}
              onClick={() => setFilterOpen(!filterOpen)}
            />
          </Grid>
          <Filter
            filterOpen={filterOpen}
            filterData={filterData}
            handleChangeFilter={handleChangeFilter}
            handleFilter={onFilterClick}
            handleClearFilters={handleClearFilters}
            handleRemove={handleRemove}
            submittedData={submittedData}
          />
        </Grid>

        <DataGrid
          rows={filteredRows}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          getRowClassName={(params) => {
            return params.row.status === "On Hold" ? "onhold-row" : "";
          }}
          sx={dataGridCustomStyles}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 20,
              },
            },
          }}
        />

        <EfficiencyLogsModal
          open={open}
          handleClose={handleClose}
          selectedPicker={selectedPicker}
        />
      </Box>
    </Box>
  );
}
